import { useMemo, useCallback, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useAccount } from 'wagmi'
import { useAppDispatch } from '~/state/hooks'
import { useSelectedConnectionType } from '~/state/user/hooks'
import { updatePendingChainId } from '~/state/user/actions'
import { useLimitSwapTokens } from '~/state/dex/hooks'
import { updateLimitSwapPair } from '~/state/dex/actions'
import { useUpdaterChainId, useSwitchChain } from '~/hooks'
import { isAddress } from 'viem'
import { compareString } from '~/utils/common'
import { getLimitOrderSupportedNetworks } from '~/utils/chain'
import { isUserRejectedError } from '~/utils/handleUserRejectedError'

type QueryParams = {
  chainId?: string
  inputAddress?: string
  outputAddress?: string
}

export const useClearQueryParams = () => {
  const router = useRouter()

  return useCallback(() => {
    if (Object.keys(router.query).length > 0) {
      const theme = Array.isArray(router.query.theme)
        ? router.query.theme[0]
        : router.query.theme
      const nextPath = theme
        ? `/limit-order?theme=${encodeURIComponent(theme)}`
        : '/limit-order'
      router.push(nextPath, undefined, { shallow: true })
    }
  }, [router.query])
}

const findTokenByAddress = (tokens: LimitSwapToken[], address: string) =>
  tokens.find((token) => compareString(token.address, address))

const useQueryParams = () => {
  const router = useRouter()
  const { chainId, inputAddress, outputAddress } = router.query as QueryParams

  return useMemo(
    () => ({
      routerChainId: chainId ? Number(chainId) : null,
      inputAddress,
      outputAddress,
    }),
    [router.query],
  )
}

export const useInitTokenPairByRouter = () => {
  const router = useRouter()
  const { routerChainId, inputAddress, outputAddress } = useQueryParams()

  const { address: isConnected } = useAccount()
  const { switchChain } = useSwitchChain()
  const chainId = useUpdaterChainId()
  const tokens = Object.values(useLimitSwapTokens(routerChainId))
  const selectedConnectionType = useSelectedConnectionType()

  const [awaitConnected, setAwaitConnected] = useState(false)
  const [awaitSetTokenPair, setAwaitSetTokenPair] = useState(false)

  const dispatch = useAppDispatch()

  const isValidTokenAddresses = useMemo(() => {
    const isAddressValid = isAddress(inputAddress) && isAddress(outputAddress)
    const isSameAddress = compareString(inputAddress, outputAddress)
    return isAddressValid && !isSameAddress
  }, [inputAddress, outputAddress])

  const supportedNetworks = getLimitOrderSupportedNetworks()

  const handleChainSwitch = useCallback(
    async (expectedChainId: number) => {
      // 如果 selectedConnectionType 有值代表上一次连接过，会触发 useEagerlyConnect 连接，等钱包连接后再触发 switchChain
      if (selectedConnectionType) {
        setAwaitConnected(true)
      } else {
        dispatch(updatePendingChainId(expectedChainId))
      }
      setAwaitSetTokenPair(isValidTokenAddresses)
    },
    [selectedConnectionType, isValidTokenAddresses],
  )

  const setTokenPair = useCallback(() => {
    const [newInputToken, newOutputToken] = [
      inputAddress,
      outputAddress,
    ].map((addr) => findTokenByAddress(tokens, addr))

    if (newInputToken && newOutputToken) {
      dispatch(
        updateLimitSwapPair({
          inputToken: newInputToken,
          outputToken: newOutputToken,
        }),
      )
    }
  }, [tokens?.length, routerChainId, inputAddress, outputAddress])

  useEffect(() => {
    if (!router.isReady || !routerChainId) return

    const isSupportedChain = supportedNetworks.includes(routerChainId)

    if (!isSupportedChain) return

    if (routerChainId !== chainId) {
      handleChainSwitch(routerChainId)
    } else if (isValidTokenAddresses) {
      setTokenPair()
    }
  }, [router.isReady, routerChainId, isValidTokenAddresses, tokens?.length])

  useEffect(() => {
    const handleWalletConnected = async () => {
      if (awaitConnected && isConnected) {
        setAwaitConnected(false)
        try {
          await switchChain({ chainId: routerChainId })
          dispatch(updatePendingChainId(routerChainId!))
        } catch (error) {
          if (!isUserRejectedError(error)) {
            throw error
          }
        }
      }
    }
    handleWalletConnected()
  }, [isConnected, awaitConnected])

  useEffect(() => {
    if (
      routerChainId === chainId &&
      awaitSetTokenPair &&
      !awaitConnected &&
      isValidTokenAddresses
    ) {
      setAwaitSetTokenPair(false)
      setTokenPair()
    }
  }, [chainId, awaitConnected, awaitSetTokenPair, isValidTokenAddresses])
}
